import React from 'react';
import { useTranslate } from '../../../mixins/translate/translate';
import { fetchPostFile, getResponseErrorMessage } from '../../../store/fetch';
import { Modal } from '../../common/Modal/Modal';
import { AlertMicro, EditPanelButton, EditPanelCell, EditPanelInput, EditPanelRow, EditPanelSection, EditPanelTextArea } from '../../elements';

/** Identifies the survey and the data point in the object tree the imported UT project is attached to. */
export interface IUtCsvImportTarget {
    surveyId: string;
    elementId?: string | undefined;
    unitId?: string | undefined;
    manifestationId?: string | undefined;
}

interface IUtCsvImportResult {
    id: string;
    measurementCount: number;
    skippedRowCount: number;
}

type DialogState =
    | { status: 'editing' }
    | { status: 'submitting' }
    | { status: 'failed'; message: string }
    | { status: 'imported'; result: IUtCsvImportResult };

function stripCsvExtension(filename: string) {
    return filename.replace(/\.csv$/i, '');
}

export function ImportUtCsvModal(props: { target: IUtCsvImportTarget; onClose: () => void; onImported: () => void }) {
    const translate = useTranslate();
    const [name, setName] = React.useState('');
    const [description, setDescription] = React.useState('');
    const [file, setFile] = React.useState<File | undefined>(undefined);
    const [dialogState, setDialogState] = React.useState<DialogState>({ status: 'editing' });

    function handleFileChanged(event: React.ChangeEvent<HTMLInputElement>) {
        const selectedFile = event.target.files?.[0];
        setFile(selectedFile);
        setDialogState({ status: 'editing' });
        if (selectedFile && !name) {
            setName(stripCsvExtension(selectedFile.name));
        }
    }

    async function save() {
        if (!file) {
            setDialogState({ status: 'failed', message: translate('project.importCsvNoFile') });
            return;
        }
        if (!name.trim() || dialogState.status === 'submitting') {
            return;
        }

        setDialogState({ status: 'submitting' });
        try {
            const response = await fetchPostFile<IUtCsvImportResult>(`/api/surveys/${props.target.surveyId}/utprojects/importCsv`, file, {
                name: name.trim(),
                description,
                elementId: props.target.elementId ?? '',
                unitId: props.target.unitId ?? '',
                manifestationId: props.target.manifestationId ?? '',
            });
            props.onImported();
            if (response.json.skippedRowCount > 0) {
                // Keep the dialog open so the user notices that not every row was imported.
                setDialogState({ status: 'imported', result: response.json });
            } else {
                props.onClose();
            }
        } catch (error) {
            setDialogState({ status: 'failed', message: getResponseErrorMessage(error) ?? translate('project.importCsvFailed') });
        }
    }

    const isSubmitting = dialogState.status === 'submitting';

    return (
        <Modal id="import-ut-csv-dialog" classNames="confirm-dialog" closeOnBackdropClick={!isSubmitting} onClose={props.onClose}>
            <Modal.Title>
                <h4>{translate('project.importCsvTitle')}</h4>
            </Modal.Title>
            <Modal.Content isScrollBarVisible={true} style={{ height: '300px' }}>
                {/* Modal.Content lays its children out with flex, so the form needs an explicit width to fill the dialog. */}
                <div className="admin-form theme-primary" style={{ width: '100%' }}>
                    {(dialogState.status === 'imported' && <ImportResult result={dialogState.result} />) || (
                        <>
                            {dialogState.status === 'failed' && (
                                <EditPanelSection>
                                    <AlertMicro severity="danger" text={dialogState.message} />
                                </EditPanelSection>
                            )}
                            <EditPanelRow>
                                <EditPanelCell rowCount={12}>
                                    <span className="ml5">{translate('project.importCsvFile')}</span>
                                    {/* Themed file input: only the append-button variant reserves room for the button next to the filename. */}
                                    <label className="field prepend-icon append-button file mt5">
                                        <span className="button">{translate('project.importCsvChooseFile')}</span>
                                        <input
                                            type="file"
                                            className="gui-file"
                                            name="file"
                                            accept=".csv,text/csv"
                                            disabled={isSubmitting}
                                            onChange={handleFileChanged}
                                        />
                                        <input
                                            type="text"
                                            className="gui-input"
                                            readOnly
                                            value={file?.name ?? ''}
                                            placeholder={translate('project.importCsvNoFileSelected')}
                                        />
                                        <label className="field-icon">
                                            <i className="fa fa-file-text-o"></i>
                                        </label>
                                    </label>
                                </EditPanelCell>
                            </EditPanelRow>
                            <EditPanelRow>
                                <EditPanelCell rowCount={12}>
                                    <EditPanelInput
                                        name="name"
                                        topLabel={translate('common.name')}
                                        placeholder={translate('common.name')}
                                        value={name}
                                        maxlength={255}
                                        disabled={isSubmitting}
                                        handleChange={(value) => setName(value)}
                                    />
                                </EditPanelCell>
                            </EditPanelRow>
                            <EditPanelRow>
                                <EditPanelCell rowCount={12}>
                                    <EditPanelTextArea
                                        name="description"
                                        topLabel={translate('common.description')}
                                        placeholder={translate('common.description')}
                                        value={description}
                                        disabled={isSubmitting}
                                        handleChange={(value) => setDescription(value)}
                                    />
                                </EditPanelCell>
                            </EditPanelRow>
                        </>
                    )}
                </div>
            </Modal.Content>
            <Modal.Actions>
                {(dialogState.status === 'imported' && (
                    <EditPanelButton small={true} primary={true} text={translate('common.close')} onClick={() => props.onClose()} />
                )) || (
                    <>
                        <EditPanelButton small={true} text={translate('common.cancel')} onClick={() => props.onClose()} />
                        <EditPanelButton
                            small={true}
                            primary={true}
                            text={translate('common.save')}
                            disabled={isSubmitting || !file || !name.trim()}
                            onClick={() => void save()}
                        />
                    </>
                )}
            </Modal.Actions>
        </Modal>
    );
}

function ImportResult(props: { result: IUtCsvImportResult }) {
    const translate = useTranslate();

    return (
        <>
            <EditPanelSection>
                <AlertMicro severity="warning" text={translate('project.importCsvSkippedWarning')} />
            </EditPanelSection>
            <EditPanelRow>
                <EditPanelCell rowCount={8}>{translate('project.importCsvImportedCount')}</EditPanelCell>
                <EditPanelCell rowCount={4}>
                    <strong>{props.result.measurementCount}</strong>
                </EditPanelCell>
            </EditPanelRow>
            <EditPanelRow>
                <EditPanelCell rowCount={8}>{translate('project.importCsvSkippedCount')}</EditPanelCell>
                <EditPanelCell rowCount={4}>
                    <strong>{props.result.skippedRowCount}</strong>
                </EditPanelCell>
            </EditPanelRow>
        </>
    );
}
